import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { MACHINERY_PRODUCTS, Product } from "@/polymet/data/machinery-products";
import { CategoryService } from "@/lib/category-service";
import { supabase } from "@/lib/supabase";
import { getSupplierProfileUrl, getProductDetailUrl } from "@/lib/navigation-helpers";
import SupplierLink from "@/polymet/components/supplier-link";
import { BrandService } from "@/lib/brand-service";
import { BrandVerificationBadge } from "@/polymet/components/brand-verification-badge";
import BreadcrumbNavigation from "@/polymet/components/breadcrumb-navigation";
import { useAuth } from "@/lib/auth-context";
import { useAnalytics } from "@/polymet/hooks/use-analytics";
import { useContactAnalytics } from "@/polymet/hooks/use-contact-analytics";
import { RFQService } from "@/lib/rfq-service";
import { useToast } from "@/hooks/use-toast";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  StarIcon,
  HeartIcon,
  ShareIcon,
  MapPinIcon,
  PhoneIcon,
  Mail,
  Globe,
  MessageCircleIcon,
  FileTextIcon,
  CheckCircleIcon,
  TagIcon,
  PrinterIcon,
  TruckIcon,
  Eye,
  EyeOff,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

import { Avatar } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import ProductCard from "@/polymet/components/product-card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import {
  ArrowLeftIcon as CarouselArrowLeftIcon,
  ArrowRightIcon as CarouselArrowRightIcon,
  CheckIcon as CarouselCheckIcon,
  HeartIcon as CarouselHeartIcon,
  ShareIcon as CarouselShareIcon,
  MapPinIcon as CarouselMapPinIcon,
  PhoneIcon as CarouselPhoneIcon,
  Mail as CarouselMail,
  Globe as CarouselGlobe,
  HelpCircleIcon,
} from "lucide-react";

export default function ProductDetailPage() {
  const { productId = "", slug = "" } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const { trackProductVisit } = useAnalytics();
  const { suggestIntent, trackContactIntent } = useContactAnalytics();
  const { toast } = useToast();
  const rfqService = new RFQService();
  
  const [product, setProduct] = useState<Product | null>(null);
  const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [breadcrumbItems, setBreadcrumbItems] = useState<Array<{ name: string; href: string }>>([]);
  const [showContactDetails, setShowContactDetails] = useState(false);
  const [isRequestingQuote, setIsRequestingQuote] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successDialogData, setSuccessDialogData] = useState<{
    productName: string;
    supplierName?: string;
  } | null>(null);
  const [supplierContactDetails, setSupplierContactDetails] = useState<{
    email?: string;
    phone?: string;
    website?: string;
    full_name?: string;
  } | null>(null);
  // Brand verification status
  const [brandRelationship, setBrandRelationship] = useState<any>(null);

  useEffect(() => {
    console.log('ProductDetailPage mounted');
    console.log('useParams result:', { productId, slug });
    console.log('location.pathname:', location.pathname);
    
    const fetchProduct = async () => {
      let foundProduct: Product | null = null;

      // If we have a slug parameter, try to fetch from database first
      if (slug) {
        try {
          console.log('Fetching product with slug:', slug);
          
          // First try with left join to supplier_profiles
          const { data, error } = await supabase
            .from('products')
            .select(`
              *,
              product_images(image_url),
              product_specifications(name, value)
            `)
            .eq('slug', slug)
            .single();

          console.log('Database query result:', { data, error });

          if (data && !error) {
            // Fetch supplier profile separately if supplier_id exists
            let supplierProfile = null;
            if (data.supplier_id) {
              const { data: supplierData, error: supplierError } = await supabase
                .from('supplier_profiles')
                .select(`
                  business_name,
                  slug,
                  logo_url,
                  website,
                  accounts(email, phone, full_name)
                `)
                .eq('id', data.supplier_id)
                .single();
                
              if (!supplierError && supplierData) {
                supplierProfile = supplierData;
                console.log('Supplier profile found:', supplierProfile);
              } else {
                console.log('No supplier profile found or error:', supplierError);
              }
            }

            // Transform database product to match Product interface
            foundProduct = {
              id: data.id,
              name: data.name,
              slug: data.slug,
              categoryId: data.category_id,
              subCategoryId: data.subcategory_id,
              productCategoryId: data.product_category_id,
              condition: data.condition as "New" | "Used" | "Refurbished",
              price: Number(data.price),
              previousPrice: data.previous_price ? Number(data.previous_price) : undefined,
              currency: data.currency || "INR",
              description: data.description || "",
              features: Array.isArray(data.features) ? data.features : [],
              specifications: data.product_specifications || [],
              images: data.product_images?.map((img: any) => img.image_url) || [],
              year: data.year,
              manufacturer: data.manufacturer || "",
              model: data.model || "",
              location: data.location || "",
              rating: Number(data.rating) || 0,
              reviewCount: data.review_count || 0,
              listedDate: data.listed_date || new Date().toISOString(),
              financing: data.financing_available || false,
              warranty: data.warranty,
              inStock: data.in_stock !== false,
              deliveryAvailable: data.delivery_available !== false,
              deliveryFee: data.delivery_fee ? Number(data.delivery_fee) : undefined,
              featured: data.featured || false,
              seller: {
                id: data.supplier_id,
                name: supplierProfile?.business_name || "Supplier",
                logo: supplierProfile?.logo_url || "",
                location: data.location || "",
                rating: 0,
                verified: false,
                joinedDate: "",
              },
              rating: data.rating || 0,
              reviewCount: data.review_count || 0,
              reviews: [],
              listedDate: data.created_at,
              financing: data.financing_available || false,
              inStock: data.in_stock ?? true,
              deliveryAvailable: data.delivery_available || false,
              featured: data.featured || false,
            } as Product;

            // Store supplier contact details if available
            if (supplierProfile?.accounts) {
              setSupplierContactDetails({
                email: supplierProfile.accounts[0]?.email,
                phone: supplierProfile.accounts[0]?.phone,
                full_name: supplierProfile.accounts[0]?.full_name,
                website: supplierProfile.website
              });
            }
            
            console.log('Product found and transformed:', foundProduct);
          } else if (error) {
            console.error('Database error:', error);
            
            // If slug lookup fails, try to find by ID if slug looks like a UUID
            if (slug.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
              console.log('Slug looks like UUID, trying ID lookup:', slug);
              const { data: idData, error: idError } = await supabase
                .from('products')
                .select(`
                  *,
                  product_images(image_url),
                  product_specifications(name, value)
                `)
                .eq('id', slug)
                .single();
                
              if (idData && !idError) {
                // Fetch supplier profile separately for ID lookup too
                let supplierProfileById = null;
                if (idData.supplier_id) {
                  const { data: supplierDataById, error: supplierErrorById } = await supabase
                    .from('supplier_profiles')
                    .select(`
                      business_name,
                      slug,
                      logo_url,
                      website,
                      accounts(email, phone, full_name)
                    `)
                    .eq('id', idData.supplier_id)
                    .single();
                    
                  if (!supplierErrorById && supplierDataById) {
                    supplierProfileById = supplierDataById;
                  }
                }

                // Use the same transformation logic
                foundProduct = {
                  id: idData.id,
                  name: idData.name,
                  slug: idData.slug,
                  categoryId: idData.category_id,
                  subCategoryId: idData.subcategory_id,
                  productCategoryId: idData.product_category_id,
                  condition: idData.condition || "New",
                  price: idData.price,
                  currency: idData.currency || "INR",
                  description: idData.description || "",
                  features: idData.features ? JSON.parse(idData.features) : [],
                  specifications: idData.product_specifications || [],
                  images: idData.product_images?.map((img: any) => img.image_url) || [],
                  year: idData.year,
                  manufacturer: idData.manufacturer || "",
                  model: idData.model || "",
                  hoursUsed: idData.hours_used,
                  location: idData.location || "",
                  seller: {
                    id: idData.supplier_id,
                    name: supplierProfileById?.business_name || "Supplier",
                    logo: supplierProfileById?.logo_url || "",
                    location: idData.location || "",
                    rating: 0,
                    verified: false,
                    joinedDate: "",
                  },
                  rating: idData.rating || 0,
                  reviewCount: idData.review_count || 0,
                  reviews: [],
                  listedDate: idData.created_at,
                  financing: idData.financing_available || false,
                  inStock: idData.in_stock ?? true,
                  deliveryAvailable: idData.delivery_available || false,
                  featured: idData.featured || false,
                } as Product;

                if (supplierProfileById?.accounts && supplierProfileById.accounts.length > 0) {
                  setSupplierContactDetails({
                    email: supplierProfileById.accounts[0]?.email,
                    phone: supplierProfileById.accounts[0]?.phone,
                    full_name: supplierProfileById.accounts[0]?.full_name,
                    website: supplierProfileById.website
                  });
                }
                console.log('Product found by ID:', foundProduct);
              }
            }
          }
        } catch (error) {
          console.error('Error fetching product by slug:', error);
        }
      }

      // Fallback to mock data for productId or if slug lookup failed
      if (!foundProduct && productId) {
        foundProduct = MACHINERY_PRODUCTS.find((p) => p.id === productId) || null;
      }

      if (foundProduct) {
        setProduct(foundProduct);

        // Track product visit for analytics
        if (user?.id) {
          trackProductVisit({
            productId: foundProduct.id,
            supplierId: foundProduct.seller?.id,
            categoryId: foundProduct.categoryId,
            subcategoryId: foundProduct.subCategoryId,
            productCategoryId: foundProduct.productCategoryId,
            source: 'direct',
            interactionType: 'view'
          });
        }

        // For demo purposes, try to fetch additional supplier info from Supabase
        // In a real implementation, this would use foundProduct.seller.id
        if (foundProduct.seller && foundProduct.seller.id) {
          fetchSupplierInfo(foundProduct.seller.id);
          
          // Check if this product has a verified brand relationship
          if (foundProduct.brand && foundProduct.brand.id) {
            fetchBrandRelationship(foundProduct.brand.id, foundProduct.seller.id);
          }
        }

        // Find related products (same product category, different ID)
        // First try to get products from database by product category
        const fetchRelatedProducts = async () => {
          try {
            if (slug) {
              // Fetch similar products from database
              const { data: similarProducts, error } = await supabase
                .from('products')
                .select(`
                  *,
                  product_images(image_url)
                `)
                .eq('product_category_id', foundProduct.productCategoryId)
                .neq('id', foundProduct.id)
                .limit(8);

              if (similarProducts && !error) {
                // Transform database products to match Product interface
                const transformedProducts = similarProducts.map((dbProduct: any) => ({
                  id: dbProduct.id,
                  name: dbProduct.name,
                  categoryId: dbProduct.category_id,
                  subCategoryId: dbProduct.subcategory_id,
                  productCategoryId: dbProduct.product_category_id,
                  condition: dbProduct.condition || "New",
                  price: dbProduct.price,
                  currency: dbProduct.currency || "INR",
                  description: dbProduct.description || "",
                  features: dbProduct.features ? JSON.parse(dbProduct.features) : [],
                  specifications: [],
                  images: dbProduct.product_images?.map((img: any) => img.image_url) || [],
                  year: dbProduct.year,
                  manufacturer: dbProduct.manufacturer || "",
                  model: dbProduct.model || "",
                  hoursUsed: dbProduct.hours_used,
                  location: dbProduct.location || "",
                  seller: {
                    id: dbProduct.supplier_id,
                    name: "Supplier",
                    logo: "",
                    location: dbProduct.location || "",
                    rating: 0,
                    verified: false,
                    joinedDate: "",
                  },
                  rating: dbProduct.rating || 0,
                  reviewCount: dbProduct.review_count || 0,
                  reviews: [],
                  listedDate: dbProduct.created_at,
                  financing: dbProduct.financing_available || false,
                  inStock: dbProduct.in_stock ?? true,
                  deliveryAvailable: dbProduct.delivery_available || false,
                  featured: dbProduct.featured || false,
                }));
                setRelatedProducts(transformedProducts);
                return;
              }
            }
          } catch (error) {
            console.error('Error fetching related products from database:', error);
          }

          // Fallback to mock data
          const related = MACHINERY_PRODUCTS.filter(
            (p) =>
              p.productCategoryId === foundProduct.productCategoryId &&
              p.id !== foundProduct.id
          ).slice(0, 8);
          setRelatedProducts(related);
        };

        fetchRelatedProducts();

        // Fetch category data from Supabase
        const categoryService = new CategoryService();
        
        // Create an async function inside useEffect
        const fetchCategoryData = async () => {
          try {
            // Initialize breadcrumbs array with proper type
            const breadcrumbs: Array<{ name: string; href: string }> = [];
            
            // Fetch category
            const category = await categoryService.getCategory(foundProduct.categoryId);
            if (category) {
              breadcrumbs.push({
                name: category.name,
                href: `/${category.slug}`,
              });

              // Fetch subcategory
              const subCategory = await categoryService.getSubcategory(foundProduct.subCategoryId);
              if (subCategory) {
                breadcrumbs.push({
                  name: subCategory.name,
                  href: `/${category.slug}/${subCategory.slug}`,
                });

                // Fetch product category
                const productCategory = await categoryService.getProductCategory(foundProduct.productCategoryId);
                if (productCategory) {
                  breadcrumbs.push({
                    name: productCategory.name,
                    href: `/${category.slug}/${subCategory.slug}/${productCategory.slug}`,
                  });
                }
              }
            }
            
            // Add current product to breadcrumb
            breadcrumbs.push({
              name: foundProduct.name,
              href: getProductDetailUrl(foundProduct.slug || foundProduct.id),
            });

            setBreadcrumbItems(breadcrumbs);
          } catch (error) {
            console.error('Error fetching category data:', error);
          }
        };
        
        // Call the async function
        fetchCategoryData();
      } else {
        console.log('No product found! Checking fallback conditions...');
        console.log('- foundProduct:', foundProduct);
        console.log('- productId:', productId);
        console.log('- slug:', slug);
      }

      console.log('Setting loading to false, final product state:', foundProduct);
      setLoading(false);
    };

    fetchProduct();
  }, [productId, slug]);

  // Handle quick quote request (create RFQ directly)
  const handleQuickQuote = async () => {
    if (!isAuthenticated) {
      // Store current location for redirect after login
      navigate('/login', { 
        state: { 
          from: location.pathname,
          message: 'Please log in to request a quote for this product.'
        }
      });
      return;
    }

    if (!product || isRequestingQuote) {
      return; // Already processing or no product
    }

    setIsRequestingQuote(true);

    try {
      // Create RFQ with prefilled product data
      const rfqData = {
        user_id: user!.id,
        title: `Quote Request for ${product.name}`,
        description: `I am interested in getting a quote for the ${product.manufacturer} ${product.model} ${product.name}${product.year ? ` (${product.year})` : ''} in ${product.condition} condition. Please provide your best price and terms.`,
        request_type: 'quote_request' as const,
        status: 'published' as const,
        category_id: product.categoryId || undefined,
        subcategory_id: product.subCategoryId || undefined,
        product_category_id: product.productCategoryId || undefined,
        quantity: 1,
        budget_min: product.price ? Math.floor(product.price * 0.8) : undefined, // 20% below listed price
        budget_max: product.price ? Math.ceil(product.price * 1.1) : undefined, // 10% above listed price
        target_price: product.price?.toString() || undefined,
        delivery_location: undefined,
        delivery_date: undefined,
        specifications: {
          'Manufacturer': product.manufacturer,
          'Model': product.model,
          'Year': product.year?.toString() || 'N/A',
          'Condition': product.condition,
          'Listed Price': `${product.price} ${product.currency || 'USD'}`
        },
        attachments: [],
        supplier_id: product.seller?.id || undefined,
      };

      const result = await rfqService.createRFQ(rfqData);

      // Track analytics for quick quote
      if (user?.id) {
        const userContext = {
          sessionDuration: 0,
          hasViewedProduct: true,
          userType: (user.user_metadata?.user_type || 'returning') as 'new' | 'returning' | 'business',
          productPrice: product.price,
          timeOnPage: 0
        };

        const suggestedIntent = suggestIntent(userContext);

        await trackContactIntent({
          productId: product.id,
          userId: user.id,
          selectedIntent: 'quote',
          suggestedIntent,
          userContext,
          timestamp: new Date().toISOString()
        });

        // Track product visit for analytics
        await trackProductVisit({
          productId: product.id,
          supplierId: product.seller?.id,
          categoryId: product.categoryId,
          subcategoryId: product.subCategoryId,
          productCategoryId: product.productCategoryId,
          source: 'direct',
          interactionType: 'rfq',
          timeSpent: 30 // Estimate 30 seconds for quick quote
        });
      }

      // Show success dialog instead of navigating
      setSuccessDialogData({
        productName: product.name,
        supplierName: product.seller?.name
      });
      setShowSuccessDialog(true);

    } catch (error) {
      console.error('Error creating direct quote request:', error);
      toast({
        title: "Error",
        description: "Failed to send quote request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsRequestingQuote(false);
    }
  };

  // Handle contact details display
  const handleShowContact = () => {
    if (!isAuthenticated) {
      // Store current location for redirect after login
      navigate('/login', { 
        state: { 
          from: location.pathname,
          message: 'Please log in to view supplier contact details.'
        }
      });
      return;
    }

    // Track contact interaction
    if (product && user?.id) {
      trackProductVisit({
        productId: product.id,
        supplierId: product.seller?.id,
        categoryId: product.categoryId,
        subcategoryId: product.subCategoryId,
        productCategoryId: product.productCategoryId,
        source: 'direct',
        interactionType: 'contact'
      });
    }

    setShowContactDetails(!showContactDetails);
  };

  // Handle supplier profile navigation
  const handleViewProfile = () => {
    if (product?.seller?.name) {
      const supplierSlug = product.seller.name.toLowerCase().replace(/\s+/g, '-');
      navigate(`/suppliers/${supplierSlug}`);
    }
  };

  const nextImage = () => {
    if (product) {
      setCurrentImageIndex((prevIndex) =>
        prevIndex === product.images.length - 1 ? 0 : prevIndex + 1
      );
    }
  };

  const prevImage = () => {
    if (product) {
      setCurrentImageIndex((prevIndex) =>
        prevIndex === 0 ? product.images.length - 1 : prevIndex - 1
      );
    }
  };

  const selectImage = (index: number) => {
    setCurrentImageIndex(index);
  };

  const formatPrice = (price: number, currency: string = "INR") => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: currency,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Fetch brand relationship information
  const fetchBrandRelationship = async (brandId: string, supplierId: string) => {
    try {
      const brandService = new BrandService();
      const relationship = await brandService.getBrandSupplierRelationship(brandId, supplierId);
      if (relationship) {
        setBrandRelationship(relationship);
      }
    } catch (error) {
      console.error('Error fetching brand relationship:', error);
    }
  };

  // Fetch additional supplier information from Supabase
  // This is a demo function - in a real implementation, we'd map the seller.id to a slug
  const fetchSupplierInfo = async (supplierId: string) => {
    try {
      // In a real app, we'd fetch supplier data from Supabase here
      console.log(`Fetching supplier info for ID: ${supplierId}`);
    } catch (error) {
      console.error("Error fetching supplier info:", error);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-3/4 mb-6"></div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <div className="h-96 bg-gray-200 rounded mb-4"></div>
            <div className="grid grid-cols-4 gap-2">
              {[1, 2, 3, 4].map((i, index) => (
                <div key={i} className="h-24 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
          <div>
            <div className="h-10 bg-gray-200 rounded w-3/4 mb-4"></div>
            <div className="h-6 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="h-8 bg-gray-200 rounded w-1/2 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-full mb-8"></div>
            <div className="h-12 bg-gray-200 rounded mb-4"></div>
            <div className="h-12 bg-gray-200 rounded mb-8"></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="h-24 bg-gray-200 rounded"></div>
              <div className="h-24 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold mb-6">Product Not Found</h1>
        <p className="mb-8">
          The product you are looking for does not exist or has been removed.
        </p>
        <Button asChild>
          <Link to="/">Return to Homepage</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <BreadcrumbNavigation items={breadcrumbItems} className="mb-6" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
        {/* Product Images */}
        <div>
          <div className="relative mb-6 bg-gray-50 rounded-lg overflow-hidden">
            <div className="aspect-square relative">
              <img
                src={product.images[currentImageIndex]}
                alt={`${product.name} - Image ${currentImageIndex + 1}`}
                className="w-full h-full object-contain p-4"
              />
            </div>
            {product.images.length > 1 && (
              <>
                <Button
                  variant="secondary"
                  size="icon"
                  className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full"
                  onClick={prevImage}
                >
                  <ArrowLeftIcon className="h-4 w-4" />
                </Button>
                <Button
                  variant="secondary"
                  size="icon"
                  className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full"
                  onClick={nextImage}
                >
                  <ArrowRightIcon className="h-4 w-4" />
                </Button>
              </>
            )}
            <div className="absolute top-4 left-4">
              <Badge
                variant={product.condition === "New" ? "default" : "secondary"}
              >
                {product.condition}
              </Badge>
            </div>
          </div>

          {/* Thumbnail Images */}
          {product.images.length > 1 && (
            <div className="grid grid-cols-4 gap-2">
              {product.images.map((image, index) => (
                <div
                  key={index}
                  className={`cursor-pointer aspect-square rounded-lg overflow-hidden border-2 ${
                    currentImageIndex === index
                      ? "border-primary"
                      : "border-gray-200"
                  }`}
                  onClick={() => selectImage(index)}
                >
                  <img
                    src={image}
                    alt={`${product.name} - Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Product Details */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Badge variant="outline">
              {product.condition === "New" ? "New" : "Used"}
            </Badge>
            {product.featured && (
              <Badge className="bg-blue-600 text-white">Featured</Badge>
            )}
          </div>

          <h1 className="text-2xl font-bold mb-4">{product.name}</h1>

          <div className="flex items-center gap-4 mb-6">
            <div className="flex items-center gap-1">
              {Array.from({ length: 5 }).map((_, index) => (
                <StarIcon
                  key={index}
                  className={`h-4 w-4 ${
                    index < Math.floor(product.rating)
                      ? "text-yellow-500 fill-yellow-500"
                      : "text-gray-300"
                  }`}
                />
              ))}
              <span className="text-sm ml-1">({product.reviewCount} reviews)</span>
            </div>
          </div>

          <div className="text-3xl font-bold text-primary mb-6">
            {formatPrice(product.price, product.currency)}
          </div>

          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <div className="text-sm text-muted-foreground flex items-center gap-1">
                <TagIcon className="h-4 w-4" />
                Manufacturer
              </div>
              <div className="font-medium">{product.manufacturer}</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground flex items-center gap-1">
                <TagIcon className="h-4 w-4" />
                Model
              </div>
              <div className="font-medium">{product.model}</div>
            </div>
            {product.year && (
              <div>
                <div className="text-sm text-muted-foreground flex items-center gap-1">
                  <TagIcon className="h-4 w-4" />
                  Year
                </div>
                <div className="font-medium">{product.year}</div>
              </div>
            )}
            {product.hoursUsed !== undefined && (
              <div>
                <div className="text-sm text-muted-foreground flex items-center gap-1">
                  <TagIcon className="h-4 w-4" />
                  Hours Used
                </div>
                <div className="font-medium">{product.hoursUsed}</div>
              </div>
            )}
            <div>
              <div className="text-sm text-muted-foreground flex items-center gap-1">
                <MapPinIcon className="h-4 w-4" />
                Location
              </div>
              <div className="font-medium">{product.location}</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground flex items-center gap-1">
                <TagIcon className="h-4 w-4" />
                Listed
              </div>
              <div className="font-medium">{formatDate(product.listedDate)}</div>
            </div>
          </div>

          <div className="space-y-3 mb-8">
            <Button 
              className="w-full h-12" 
              size="lg" 
              onClick={handleQuickQuote}
              disabled={isRequestingQuote}
            >
              <FileTextIcon className="h-5 w-5 mr-2" />
              {isRequestingQuote ? "Sending Request..." : "Quick Quote"}
            </Button>
          </div>

          {/* Seller Information */}
          <div className="border rounded-lg p-4">
            <h3 className="font-medium mb-3">Supplier</h3>
            <div className="flex items-center gap-3 mb-3">
              <Avatar className="h-10 w-10">
                <img
                  src={product.seller.logo || "https://github.com/polymet-ai.png"}
                  alt={product.seller.name}
                />
              </Avatar>
              <div>
                <div className="font-medium">{product.seller.name}</div>
                <div className="text-sm text-muted-foreground">Member since {product.seller.joinedDate}</div>
              </div>
            </div>
            <div className="flex items-center gap-4 mb-3">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <StarIcon
                    key={i}
                    className={`h-4 w-4 ${i < Math.floor(product.seller.rating) ? "text-yellow-500 fill-yellow-500" : "text-gray-300"}`}
                  />
                ))}
                <span className="text-sm text-muted-foreground">
                  {product.seller.rating} ({product.seller.reviewCount || 0} reviews)
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2 mb-4">
              <MapPinIcon className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">{product.seller.location}</span>
            </div>

            <div className="space-y-2">
              <Button
                variant="outline"
                className="w-full"
                onClick={handleViewProfile}
              >
                View Supplier Profile
              </Button>
              
              <Button
                variant="outline"
                className="w-full"
                onClick={handleShowContact}
              >
                {showContactDetails ? (
                  <>
                    <EyeOff className="h-4 w-4 mr-2" />
                    Hide Contact Details
                  </>
                ) : (
                  <>
                    <Eye className="h-4 w-4 mr-2" />
                    Show Contact Details
                  </>
                )}
              </Button>

              {showContactDetails && supplierContactDetails && (
                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-sm mb-2">Contact Information</h4>
                  <div className="space-y-1 text-sm">
                    {supplierContactDetails.email && (
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        <a href={`mailto:${supplierContactDetails.email}`} className="text-blue-600 hover:underline">
                          {supplierContactDetails.email}
                        </a>
                      </div>
                    )}
                    {supplierContactDetails.phone && (
                      <div className="flex items-center gap-2">
                        <PhoneIcon className="h-4 w-4 text-muted-foreground" />
                        <a href={`tel:${supplierContactDetails.phone}`} className="text-blue-600 hover:underline">
                          {supplierContactDetails.phone}
                        </a>
                      </div>
                    )}
                    {supplierContactDetails.website && (
                      <div className="flex items-center gap-2">
                        <Globe className="h-4 w-4 text-muted-foreground" />
                        <a href={supplierContactDetails.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                          Visit Website
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Key Features */}
          {product.features && product.features.length > 0 && (
            <div className="mt-6">
              <h3 className="font-medium mb-3">Key Features</h3>
              <ul className="space-y-2">
                {product.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm">
                    <CheckIcon className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Product Information Tabs */}
      <div className="mb-12">
        <Accordion type="single" collapsible defaultValue="description">
          <AccordionItem value="description">
            <AccordionTrigger>Product Description</AccordionTrigger>
            <AccordionContent>
              <div className="prose max-w-none">
                <p>{product.description}</p>
              </div>
            </AccordionContent>
          </AccordionItem>

          {product.specifications && product.specifications.length > 0 && (
            <AccordionItem value="specifications">
              <AccordionTrigger>Technical Specifications</AccordionTrigger>
              <AccordionContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {product.specifications.map((spec, index) => (
                    <div key={index} className="flex justify-between py-2 border-b border-gray-100">
                      <span className="font-medium">{spec.name}</span>
                      <span className="text-muted-foreground">{spec.value}</span>
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          )}

          <AccordionItem value="delivery">
            <AccordionTrigger>Delivery & Warranty</AccordionTrigger>
            <AccordionContent>
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <TruckIcon className="h-5 w-5 text-green-600" />
                  <div>
                    <div className="font-medium">
                      {product.deliveryAvailable ? "Delivery Available" : "Pickup Only"}
                    </div>
                    {product.deliveryFee && (
                      <div className="text-sm text-muted-foreground">
                        Delivery fee: {formatPrice(product.deliveryFee, product.currency)}
                      </div>
                    )}
                  </div>
                </div>
                
                {product.warranty && (
                  <div className="flex items-center gap-2">
                    <CheckCircleIcon className="h-5 w-5 text-green-600" />
                    <div>
                      <div className="font-medium">Warranty Included</div>
                      <div className="text-sm text-muted-foreground">{product.warranty}</div>
                    </div>
                  </div>
                )}

                {product.financing && (
                  <div className="flex items-center gap-2">
                    <CheckCircleIcon className="h-5 w-5 text-green-600" />
                    <div>
                      <div className="font-medium">Financing Available</div>
                      <div className="text-sm text-muted-foreground">Contact supplier for details</div>
                    </div>
                  </div>
                )}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6">Related Products</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {relatedProducts.map((relatedProduct) => (
              <ProductCard key={relatedProduct.id} product={relatedProduct} />
            ))}
          </div>
        </div>
      )}

      {/* Success Dialog */}
      <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircleIcon className="h-5 w-5 text-green-600" />
              Quote Request Sent!
            </DialogTitle>
            <DialogDescription>
              Your quote request for <strong>{successDialogData?.productName}</strong> has been sent to{" "}
              <strong>{successDialogData?.supplierName}</strong>.
              You will receive a response via email within 24 hours.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowSuccessDialog(false)}
            >
              Close
            </Button>
            <Button
              type="button"
              onClick={() => {
                setShowSuccessDialog(false);
                navigate('/dashboard/rfqs');
              }}
            >
              View My Requests
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}